import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";

import {
  Button,
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownItems,
} from "@transfr-inc/dashboard-components/forms";

import { CareerMatchesList } from "@features/career-matches/components/career-matches.list";
import { Card, CardBody, CardFooter, CardHeader, Loader } from "@features/ui";
import { CareerMatchesStartOverConfirmationModal } from "@pages/journey-map/views/career-matches-start-over-confirmation.modal";

import { A11yAlertAnnouncer } from "@features/a11y";
import { useCareerPlan } from "@features/career-details/api/use-career-plan";
import { useCareerMatches } from "@features/career-matches/api";
import { useUserPreliminaryCareers } from "@features/interest-inventory/api/use-user-preliminary-careers";
import { useBreakpoints } from "@hooks/use-breakpoints";
import { useSessionValue } from "@hooks/use-session-value";
import { buttonTracking } from "@lib/tracking.helpers";
import { useStartOver } from "@pages/journey-map/api/use-start-over";

import { SESSION_VALUE_KEYS } from "src/constants";

import "./index.scss";

const sortOptions = [
  {
    label: "Best Match",
    value: "best_match",
  },
  {
    label: "Job Outlook: High to Low",
    value: "job_outlook-",
  },
  {
    label: "Job Outlook: Low to High",
    value: "job_outlook",
  },
  {
    label: "Pay: High to Low",
    value: "salary-",
  },
  {
    label: "Pay: Low to High",
    value: "salary",
  },
  {
    label: "Alphabetical: A to Z",
    value: "title",
  },
  {
    label: "Alphabetical: Z to A",
    value: "title-",
  },
];

export const CareerMatches = ({ onCareerSelected, onClose, onStartOver }) => {
  const [fetchingNextPage, setFetchingNextPage] = useState();
  const [openConfirmationModal, setOpenConfirmationModal] = useState();
  const [sort, setSort] = useSessionValue(
    SESSION_VALUE_KEYS.careerMatchesSort,
    {
      defaultValue: sortOptions[0],
    },
  );

  const { isMobile } = useBreakpoints();

  const pageSize = useMemo(() => (isMobile ? 5 : 10), [isMobile]);

  const { data: careerPlan } = useCareerPlan();
  const {
    data: careerMatches,
    isError: careerMatchesErrored,
    isFetching: fetchingCareerMatches,
    fetchNextPage,
    hasNextPage,
  } = useCareerMatches({
    limit: pageSize,
    sort: sort.value,
  });
  const { data: preliminaryCareers, isFetching: fetchingPreliminaryCareers } =
    useUserPreliminaryCareers({ includeRelatedCareers: true });

  const { isStartingOver, startOver } = useStartOver();

  const onCareerClick = (career, index) => {
    onCareerSelected(career, index);
  };

  const closeConfirmationModal = () => setOpenConfirmationModal();

  const onConfirmStartOver = async () => {
    await startOver({ restartInterestInventory: true });
    closeConfirmationModal();
    onStartOver?.();
  };

  const onNextPage = () => {
    fetchNextPage();
    setFetchingNextPage(true);
  };

  useEffect(() => {
    // Show skeleton loader when refreshing the careers list
    // This to avoid flickering in the list when a career has set to hide in details page
    if (fetchingNextPage && !fetchingCareerMatches) {
      setFetchingNextPage();
    }
  }, [fetchingCareerMatches]);

  return (
    <>
      <Card className="career-matches-card">
        <CardHeader
          title={<h1 tabIndex={-1}>Career Matches</h1>}
          toolbar={
            <Button
              onClick={onClose}
              icon={["fa-regular", "xmark"]}
              title="close"
              {...buttonTracking("career-matches", "close")}
            />
          }
        >
          <Dropdown selected={sort} onOptionSelected={setSort}>
            <DropdownButton aria-label="sort">
              Sort: <span className="sort-label">{sort.label}</span>
            </DropdownButton>
            <DropdownItems>
              {sortOptions.map((sortOption) => (
                <DropdownItem
                  key={sortOption.value}
                  value={sortOption}
                  propName="value"
                >
                  {sortOption.label}
                </DropdownItem>
              ))}
            </DropdownItems>
          </Dropdown>
        </CardHeader>
        <CardBody className="career-matches-card-body">
          <CareerMatchesList
            careerMatches={careerMatches}
            preliminaryCareers={preliminaryCareers}
            careerSelected={careerPlan}
            isCustomSorted={sort?.value !== sortOptions[0]?.value}
            isLoading={
              fetchingPreliminaryCareers ||
              (fetchingCareerMatches && !fetchingNextPage)
            }
            isError={careerMatchesErrored}
            onCareerClick={onCareerClick}
          />
        </CardBody>
        <CardFooter className="career-matches-footer">
          <div className="actions-container">
            {(hasNextPage || fetchingCareerMatches) && (
              <Button
                icon={["fa-regular", "arrow-down"]}
                loader={fetchingCareerMatches}
                onClick={onNextPage}
                disabled={fetchingCareerMatches}
                aria-live="assertive"
                aria-busy={fetchingCareerMatches}
                aria-label={
                  fetchingCareerMatches
                    ? "loading careers"
                    : "load more careers"
                }
                {...buttonTracking("career-matches", "load-more-careers")}
              >
                Load More Careers
              </Button>
            )}
            <Button
              icon={["fa-regular", "arrow-rotate-left"]}
              onClick={() => setOpenConfirmationModal(true)}
              {...buttonTracking("career-matches", "start-over")}
            >
              Start Over
            </Button>
          </div>
        </CardFooter>
      </Card>
      <Loader show={isStartingOver} text={"removing matches..."} />
      <CareerMatchesStartOverConfirmationModal
        open={openConfirmationModal}
        onConfirm={onConfirmStartOver}
        onCancel={closeConfirmationModal}
        onClose={closeConfirmationModal}
        careerTitle={careerPlan.career?.title}
      />
      {!fetchingCareerMatches && (
        <A11yAlertAnnouncer timeoutMs={500}>
          {pageSize} new careers loaded
        </A11yAlertAnnouncer>
      )}
    </>
  );
};

CareerMatches.displayName = "Career Matches Card";

CareerMatches.propTypes = {
  onCareerSelected: PropTypes.func,
  onClose: PropTypes.func,
  onStartOver: PropTypes.func,
};
